import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '~/auth/use-auth';
import { useAccounts } from '../../../-hooks/use-accounts';
import type { Account } from '../../../-types/account';
import { useTransfer } from '../../-hooks/use-transfer';

const MEDIUM_LABELS: Record<string, string> = {
  bancolombia: 'Bancolombia',
  card: 'Tarjetas',
  polygon: 'Polygon',
  virtual: 'Cuentas Virtuales',
};

const MEDIUMS = ['bancolombia', 'card', 'polygon', 'virtual'] as const;
type Medium = (typeof MEDIUMS)[number];

const MANUAL_ENTRY_VALUE = '__manual__';

const transferSchema = z.object({
  destinationUrn: z
    .string()
    .min(1, 'La URN de destino es requerida')
    .regex(
      /^did:bloque:account:/,
      'La URN debe ser una cuenta de Bloque válida',
    ),
  amount: z
    .string()
    .min(1, 'El monto es requerido')
    .refine((val) => !Number.isNaN(Number(val)) && Number(val) > 0, {
      message: 'El monto debe ser un número mayor a 0',
    }),
  asset: z.enum(['DUSD/6', 'KSM/12']),
});

type TransferForm = z.infer<typeof transferSchema>;

interface TransferDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sourceUrn: string;
  availableAssets: string[];
  balance?: Record<string, { current: string }>;
}

type GenericAccount = Account<
  { id: string; reference_code?: string },
  { name?: string }
>;

interface AccountsResponse {
  accounts: GenericAccount[];
}

function useAllAccounts(holderUrn: string) {
  const bancolombiaQuery = useAccounts<AccountsResponse>({
    holder_urn: holderUrn,
    medium: 'bancolombia',
  });

  const cardQuery = useAccounts<AccountsResponse>({
    holder_urn: holderUrn,
    medium: 'card',
  });

  const polygonQuery = useAccounts<AccountsResponse>({
    holder_urn: holderUrn,
    medium: 'polygon',
  });

  const virtualQuery = useAccounts<AccountsResponse>({
    holder_urn: holderUrn,
    medium: 'virtual',
  });

  const isLoading =
    bancolombiaQuery.isLoading ||
    cardQuery.isLoading ||
    polygonQuery.isLoading ||
    virtualQuery.isLoading;

  const accountsByMedium: Record<Medium, GenericAccount[]> = {
    bancolombia: bancolombiaQuery.data?.data?.accounts ?? [],
    card: cardQuery.data?.data?.accounts ?? [],
    polygon: polygonQuery.data?.data?.accounts ?? [],
    virtual: virtualQuery.data?.data?.accounts ?? [],
  };

  return { accountsByMedium, isLoading };
}

function getAccountDisplayName(account: GenericAccount): string {
  if (account.metadata?.name) {
    return account.metadata.name;
  }
  if (account.details?.reference_code) {
    return `****${account.details.reference_code.slice(-4)}`;
  }
  return account.urn.slice(-12);
}

export function TransferDialog({
  isOpen,
  onClose,
  sourceUrn,
  availableAssets,
  balance,
}: TransferDialogProps) {
  const { user } = useAuth();
  const transfer = useTransfer();
  const [isManualEntry, setIsManualEntry] = useState(false);
  const { accountsByMedium, isLoading: isLoadingAccounts } = useAllAccounts(
    user?.urn ?? '',
  );

  const validAssets = availableAssets.filter(
    (asset): asset is 'DUSD/6' | 'KSM/12' =>
      asset === 'DUSD/6' || asset === 'KSM/12',
  );

  const form = useForm<TransferForm>({
    resolver: zodResolver(transferSchema),
    defaultValues: {
      destinationUrn: '',
      amount: '',
      asset: validAssets[0] || 'DUSD/6',
    },
  });

  const selectedAsset = form.watch('asset');
  const decimals = selectedAsset === 'DUSD/6' ? 6 : 12;
  const currentBalance = balance?.[selectedAsset]?.current ?? '0';
  const normalizedBalance = Number(currentBalance) * 10 ** -decimals;

  const onSubmit = async (data: TransferForm) => {
    const amountInSmallestUnit = Math.floor(
      Number(data.amount) * 10 ** decimals,
    ).toString();

    transfer.mutate(
      {
        sourceUrn,
        destinationUrn: data.destinationUrn,
        amount: amountInSmallestUnit,
        asset: data.asset,
      },
      {
        onSuccess: (response) => {
          if (!response.error) {
            form.reset();
            onClose();
          }
        },
      },
    );
  };

  const handleClose = () => {
    form.reset();
    setIsManualEntry(false);
    onClose();
  };

  const handleAccountSelect = (value: string) => {
    if (value === MANUAL_ENTRY_VALUE) {
      setIsManualEntry(true);
      form.setValue('destinationUrn', '');
    } else {
      setIsManualEntry(false);
      form.setValue('destinationUrn', value);
    }
  };

  const getSelectValue = () => {
    if (isManualEntry) return MANUAL_ENTRY_VALUE;
    const destinationUrn = form.getValues('destinationUrn');
    if (destinationUrn === '') return '';
    const allAccounts = Object.values(accountsByMedium).flat();
    const isKnownAccount = allAccounts.some(
      (acc) => acc.urn === destinationUrn,
    );
    if (isKnownAccount) return destinationUrn;
    return MANUAL_ENTRY_VALUE;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Transferir fondos</DialogTitle>
          <DialogDescription>
            Transfiere fondos a otra cuenta de Bloque
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="destinationUrn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cuenta destino</FormLabel>
                  <Select
                    onValueChange={handleAccountSelect}
                    value={getSelectValue()}
                    disabled={isLoadingAccounts}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            isLoadingAccounts
                              ? 'Cargando cuentas...'
                              : 'Selecciona una cuenta'
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MEDIUMS.map((medium) => {
                        const accounts = accountsByMedium[medium].filter(
                          (acc) => acc.urn !== sourceUrn,
                        );
                        if (accounts.length === 0) return null;

                        return (
                          <SelectGroup key={medium}>
                            <SelectLabel>{MEDIUM_LABELS[medium]}</SelectLabel>
                            {accounts.map((account) => (
                              <SelectItem key={account.urn} value={account.urn}>
                                {getAccountDisplayName(account)}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        );
                      })}
                      <SelectGroup>
                        <SelectLabel>Otra cuenta</SelectLabel>
                        <SelectItem value={MANUAL_ENTRY_VALUE}>
                          Ingresar URN manualmente
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {isManualEntry && (
                    <Input
                      className="mt-2"
                      placeholder="did:bloque:account:..."
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="asset"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Activo</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona un activo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {validAssets.map((asset) => (
                        <SelectItem key={asset} value={asset}>
                          {asset === 'DUSD/6' ? 'USD' : 'KSM'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Monto{' '}
                    <span className="text-muted-foreground text-xs">
                      (Disponible: ${normalizedBalance.toFixed(2)})
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={transfer.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={transfer.isPending}>
                {transfer.isPending ? 'Transfiriendo...' : 'Transferir'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
